export interface IPriceFormatter {
    currency: string;
    decimalSeparator?: string;
    locale: string;
    numberOfDecimals?: number;
    hideZeroDecimals?: boolean;
}

const ZERO_DECIMALS = '00';

export class PriceFormatter {
    private decimalSeparator?: string;
    private hideZeroFraction?: boolean;
    private formatter: Intl.NumberFormat;

    constructor(config: IPriceFormatter) {
        const { locale, currency, numberOfDecimals, decimalSeparator, hideZeroDecimals } = config;
        this.decimalSeparator = decimalSeparator;
        this.hideZeroFraction = hideZeroDecimals;
        this.formatter = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            maximumFractionDigits: numberOfDecimals,
            minimumFractionDigits: numberOfDecimals
        });
    }

    private replaceDecimalSeparator = (
        decimalSeparator: string,
        parts: Intl.NumberFormatPart[]
    ) => {
        const { value: fraction } = parts.find(
            ({ type }: Intl.NumberFormatPart) => type === 'fraction'
        ) as Intl.NumberFormatPart;

        return this.hideZeroFraction && fraction === ZERO_DECIMALS
            ? null
            : this.decimalSeparator || decimalSeparator;
    };

    private replaceFraction = (fraction: string) =>
        this.hideZeroFraction && fraction === ZERO_DECIMALS ? null : fraction;

    private replacePart = (
        { type, value }: Intl.NumberFormatPart,
        index: number,
        parts: Intl.NumberFormatPart[]
    ) => {
        switch (type) {
            case 'decimal':
                return this.replaceDecimalSeparator(value, parts);
            case 'fraction':
                return this.replaceFraction(value);
            default:
                return value;
        }
    };

    public formatPriceWithCurrency = (price: number) => {
        try {
            return this.formatter.formatToParts(price).map(this.replacePart).join('');
        } catch {
            // For older versions of Safari.
            return this.formatter.format(price);
        }
    };
}
